import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { RELEVANCES } from 'constants/date';
import { predefinedTemplates } from 'constants/entities';
import { MAPBOX_MAP_CONTAINER } from 'constants/map';
import { useAppDispatch, useAppSelector } from 'hooks';
import { ILayerQuickFilterSettings } from 'interfaces/localStorage';
import MapProvider from 'providers/map/MapProvider';
import { MapRefProvider } from 'providers/map/MapRefProvider';
import { getAttributesThunk } from 'store/slices/map/actions';
import { settingsActions } from 'store/slices/mapV2/mapReducer/settingsSlice';
import { initialState as mapSettingsInitialState } from 'store/slices/mapV2/mapReducer/settingsSlice/constants';
import { stateActions as toolsStateActions } from 'store/slices/mapV2/mapReducer/toolsReducer/stateSlice';
import { mapEntitiesActions } from 'store/slices/mapV2/tabsReducer/layersReducer/mapEntitiesSlice';
import {
  getInitialMapEntitiesThunk,
  getPredefinedTemplateThunk,
  getSharedMapEntityThunk,
} from 'store/slices/mapV2/tabsReducer/layersReducer/mapEntitiesSlice/actions';
import { stateActions as layersStateActions } from 'store/slices/mapV2/tabsReducer/layersReducer/stateSlice';
import { timelineTypesSelector } from 'store/slices/mapV2/tabsReducer/layersReducer/stateSlice/selectors';
import { getOperationalRangesThunk } from 'store/slices/mapV2/tabsReducer/operationalSlice/actions';
import { modalsActions } from 'store/slices/service/modalsSlice';
import { MapDrawModes } from 'types';

import { MapFooter } from 'components/Map/MapFooter';
import { MapHeader } from 'components/Map/MapHeader';
import { MapSidebar } from 'components/Map/MapSidebar';
import { getDateRangeFromRelevance, useLocalStorageDates } from 'utils';
import { useLayerQuickFilterLocalStorage } from 'utils/localStorage';
import { useMapSettingsLocalStorage } from 'utils/localStorage';

import { Map } from './Map';

import './style.scss';

const MapPage = () => {
  const { entityId } = useParams<{ entityId: string }>();
  const [quickFilterSettings] = useLayerQuickFilterLocalStorage();
  const [areEntitiesLoaded, setAreEntitiesLoaded] = useState(false);
  const [mapLocalSettings] = useMapSettingsLocalStorage(
    mapSettingsInitialState
  );
  const [startDate, endDate] = useLocalStorageDates(quickFilterSettings);
  const { objects: isTimeline } = useAppSelector(timelineTypesSelector);
  const [storedSettings, setStoredSettings] =
    useState<ILayerQuickFilterSettings | null>(null);
  const isTimelineRef = useRef(isTimeline);

  const dispatch = useAppDispatch();

  const getFiltersCallback = (deps?: any[]) =>
    useCallback(() => {
      const { relevance } = quickFilterSettings;

      // Remove when BE returns partial tree data with filters applied
      if (!relevance || relevance === RELEVANCES.ALL_TIME) {
        return null;
      }

      const properDateRange = getDateRangeFromRelevance(
        relevance,
        Boolean(startDate && endDate)
      );
      const filters = {
        createdAtFilter: {
          gte: properDateRange?.[0] ?? startDate,
          lte: properDateRange?.[1] ?? endDate,
        },
      };

      return filters;
    }, deps ?? []);

  const getFilters = getFiltersCallback();

  const getMapData = useCallback(async () => {
    const filters = getFilters() ?? {};

    dispatch(layersStateActions.setFilters(filters));
    dispatch(getAttributesThunk());
    dispatch(getOperationalRangesThunk());

    // make sure all predefined templates are loaded before getting map entities
    await dispatch(getPredefinedTemplateThunk(predefinedTemplates.MAP_LAYER));
    await dispatch(getPredefinedTemplateThunk(predefinedTemplates.MAP_OBJECT));

    try {
      await dispatch(
        getInitialMapEntitiesThunk({
          ...filters,
        })
      );

      if (entityId) {
        await dispatch(getSharedMapEntityThunk(Number(entityId))).then(() =>
          setAreEntitiesLoaded(true)
        );
      }

      // enable when histogram enabled
      // await dispatch(getMapEntitiesHistogramThunk());
      dispatch(layersStateActions.setIsMapDataLoaded(true));
    } catch (e) {
      console.error(e);
    }
  }, [getFilters]);

  const getMapSettings = useCallback(() => {
    dispatch(settingsActions.setShowAim(mapLocalSettings.showAim));
    dispatch(
      settingsActions.setShowCursors(mapLocalSettings.showCustomCursors)
    );
    dispatch(settingsActions.setShowLabels(mapLocalSettings.showLabels));
    dispatch(settingsActions.setShowObjects(mapLocalSettings.showObjects));
    dispatch(
      settingsActions.setClusterObjects(mapLocalSettings.clusterObjects)
    );
    dispatch(settingsActions.setShowObjectIDs(mapLocalSettings.showObjectIDs));
  }, [dispatch]);

  useEffect(() => {
    getMapData();
    getMapSettings();

    return () => {
      dispatch(
        toolsStateActions.setInteractionMode(MapDrawModes.simple_select)
      );
      dispatch(mapEntitiesActions.resetState);
      dispatch(modalsActions.setModals({}));
    };
  }, [getMapData, getMapSettings]);

  useEffect(() => {
    if (!isTimeline && isTimelineRef.current) {
      dispatch(
        getInitialMapEntitiesThunk(
          storedSettings?.relevance === RELEVANCES.ALL_TIME
            ? {}
            : {
                createdAtFilter: {
                  gte: storedSettings?.startDate,
                  lte: storedSettings?.endDate,
                },
              }
        )
      );
    }
    if (isTimeline && !isTimelineRef.current) {
      setStoredSettings(quickFilterSettings);
    }
  }, [isTimeline, storedSettings]);

  useEffect(() => {
    isTimelineRef.current = isTimeline;
  }, [isTimeline]);

  return (
    <MapRefProvider>
      <MapProvider>
        <>
          <MapHeader />
          <div className="relative flex w-full h-full">
            <MapSidebar />
            <div className="map-container relative overflow-hidden">
              <div
                id={MAPBOX_MAP_CONTAINER}
                className="map-container__wrapper flex flex-col"
              >
                <Map areEntitiesLoaded={areEntitiesLoaded} />
              </div>
            </div>
          </div>
          <MapFooter />
        </>
      </MapProvider>
    </MapRefProvider>
  );
};

export default MapPage;
